import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { leaveTypesQueryOptions } from '../api/leaveTypes.api';
import { useLeaveTypeActionModals } from '../hooks/useLeaveTypeActionModals';
import type { LeaveType } from '../types/leaveType.types';
import { getLeaveTypeColumns } from './LeaveTypeColumns';
import { LeaveTypeDeleteConfirmation } from './LeaveTypeDeleteConfirmation';
import { LeaveTypeFormModal } from './LeaveTypeFormModal';

const route = getRouteApi('/_auth/settings/leave-types');

const toSearchableValues = (leaveType: LeaveType) => [
  leaveType.name,
  leaveType.code,
  leaveType.description,
];

export function LeaveTypeListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    leaveTypeToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useLeaveTypeActionModals();

  const { data, isLoading, error, refetch } = useQuery(leaveTypesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getLeaveTypeColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Leave Types"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by code or name',
          }}
          createAction={{ label: 'Add Leave Type', onClick: openCreateModal }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load leave types"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
          />
        )}
      </div>

      <LeaveTypeFormModal
        leaveType={modalState?.leaveType ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <LeaveTypeDeleteConfirmation
        leaveType={leaveTypeToDelete}
        open={leaveTypeToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </>
  );
}
